// firstPassRate.js — Modal content for First Pass Rate KPI
// Place at: src/components/modalContent/productionMilestonesModalComponents/firstPassRate.js

import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  FIRST_PASS_RATE_TREND,
  FPR_BY_BUILDING,
  FPR_FAILURE_TYPES,
} from "../../../mockData/mockProductionMilestonesData";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">{label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: "#cc0000" }}
        />
        {payload[0].value}% first pass rate
      </div>
    </div>
  );
};

const FirstPassRate = ({ goals }) => {
  const target = goals?.firstPassRate || 92.0;
  const current = FIRST_PASS_RATE_TREND[FIRST_PASS_RATE_TREND.length - 1];
  const avgFpr = (
    FIRST_PASS_RATE_TREND.reduce((s, d) => s + d.fpr, 0) / 12
  ).toFixed(1);
  const best = FIRST_PASS_RATE_TREND.reduce((a, b) => (b.fpr > a.fpr ? b : a));
  const monthsOnTarget = FIRST_PASS_RATE_TREND.filter(
    (d) => d.fpr >= target,
  ).length;

  const currentVariant =
    current.fpr >= target
      ? "ok"
      : current.fpr >= target - 3
        ? "amber"
        : "alert";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Stat band */}
      <div className="pml-modal-stat-band pml-modal-stat-band--4">
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Current Month</span>
          <span
            className={`pml-modal-stat-value pml-modal-stat-value--${currentVariant}`}
          >
            {current.fpr}%
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Target</span>
          <span className="pml-modal-stat-value">{target}%</span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">12-Month Avg</span>
          <span
            className={`pml-modal-stat-value ${parseFloat(avgFpr) >= target ? "pml-modal-stat-value--ok" : "pml-modal-stat-value--amber"}`}
          >
            {avgFpr}%
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Best Month</span>
          <span className="pml-modal-stat-value pml-modal-stat-value--ok">
            {best.month} — {best.fpr}%
          </span>
        </div>
      </div>

      {/* Line chart */}
      <p className="pml-modal-sub-title">12-Month FPR Trend</p>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={FIRST_PASS_RATE_TREND}
            margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v}%`}
              domain={[84, 94]}
              width={38}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={target}
              stroke="#1a1a1a"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              label={{
                value: `Target ${target}%`,
                position: "insideTopRight",
                fontSize: 11,
                fill: "#1a1a1a",
              }}
            />
            <Line
              type="monotone"
              dataKey="fpr"
              stroke="#cc0000"
              strokeWidth={2}
              dot={{ r: 4, fill: "#cc0000", strokeWidth: 0 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Two-column lower section */}
      <div className="fpr-lower-grid">
        {/* By building */}
        <div>
          <p className="pml-modal-sub-title">Current Month by Building</p>
          <table className="pml-modal-table">
            <thead>
              <tr>
                <th>Building</th>
                <th>FPR</th>
                <th>Target</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {FPR_BY_BUILDING.map((b) => {
                const ok = b.fpr >= b.target;
                return (
                  <tr key={b.building}>
                    <td>{b.building}</td>
                    <td style={{ fontWeight: 700 }}>{b.fpr}%</td>
                    <td>{b.target}%</td>
                    <td
                      className={
                        ok ? "pml-variance-cell--pos" : "pml-variance-cell--neg"
                      }
                    >
                      {ok ? "On Target" : "Below"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize: 12, color: "#a0aec0", marginTop: 8 }}>
            {monthsOnTarget} of 12 months met the {target}% target.
          </div>
        </div>

        {/* Failure types */}
        <div>
          <p className="pml-modal-sub-title">
            Utlities Failure Count (This Month)
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {FPR_FAILURE_TYPES.map((f) => (
              <div key={f.type}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}
                  >
                    {f.type}
                  </span>
                  <span
                    style={{ fontSize: 13, color: "#718096", fontWeight: 500 }}
                  >
                    {f.count} ({f.pct}%)
                  </span>
                </div>
                <div className="pml-progress-bar">
                  <div
                    className="pml-progress-fill"
                    style={{
                      width: `${f.pct}%`,
                      background: "#cc0000",
                      opacity: 0.7,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstPassRate;
